import React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Field,
  MiniSelect,
  NumberInput,
  BiometryList,
  SectionHeading,
  TwoColumnLayout,
  ScreeningSection,
  FetalAnatomySection,
} from './primitives';
import {
  T3_BIOMETRY,
  OPTIONS,
  amnioticFluidFromSdpCm,
} from './lib/form-config';
import { useReportData } from './lib/report-context';

function numVal(m: Record<string, unknown>, key: string): number | undefined {
  const v = m[key];
  if (v == null) {
    return undefined;
  }
  const n = Number(v);
  return isNaN(n) ? undefined : n;
}

function blurHandler(
  key: string,
  onUpdate?: (u: Record<string, unknown>) => void
) {
  return (e: React.FocusEvent<HTMLInputElement>) =>
    onUpdate?.({ [key]: e.target.value ? parseFloat(e.target.value) : null });
}

function VitalityBPP() {
  const { fetalAssessment, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const hr = fetalAssessment.heartRate
    ? Math.round(fetalAssessment.heartRate)
    : undefined;

  return (
    <>
      <SectionHeading>sections.vitalityBpp</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field
          label="fields.cardiacActivity"
          explainFindingKey="cardiac_activity"
        >
          <MiniSelect
            defaultValue={
              sr.cardiac_activity_present === true
                ? 'present-regular'
                : sr.cardiac_activity_present === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.cardiacActivity}
            onChange={(e) =>
              onUpdateFindings?.({
                cardiac_activity_present:
                  e.target.value === 'absent' ? false : !!e.target.value,
              })
            }
          />
        </Field>
        <Field label="fields.heartRate" explainFindingKey="cardiac_activity">
          <NumberInput
            defaultValue={hr}
            step={1}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                heart_rate: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="fields.fetalMovement">
          <MiniSelect
            defaultValue={(sr.fetal_movement_quality as string) || ''}
            options={OPTIONS.fetalMovement}
            onChange={(e) =>
              onUpdateFindings?.({
                fetal_movement_quality: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.fetalTone">
          <MiniSelect
            defaultValue={
              sr.fetal_tone === true
                ? 'normal'
                : sr.fetal_tone === false
                  ? 'absent'
                  : ''
            }
            options={[
              { value: 'normal', label: 'options.normal' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ fetal_tone: e.target.value === 'normal' })
            }
          />
        </Field>
        <Field label="fields.breathingMvmts">
          <MiniSelect
            defaultValue={
              sr.fbm === true ? 'present' : sr.fbm === false ? 'absent' : ''
            }
            options={[
              { value: 'present', label: 'options.present30s' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ fbm: e.target.value === 'present' })
            }
          />
        </Field>
        <Field label="fields.bppScore">
          <NumberInput
            defaultValue={
              sr.bpp_score != null ? Number(sr.bpp_score) : undefined
            }
            min={0}
            max={8}
            step={2}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                bpp_score: e.target.value ? parseInt(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="fields.grossMovements">
          <MiniSelect
            defaultValue={
              sr.gross_movements === true
                ? 'present'
                : sr.gross_movements === false
                  ? 'absent'
                  : ''
            }
            options={[
              { value: 'present', label: 'options.gte3In30min' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                gross_movements: e.target.value === 'present',
              })
            }
          />
        </Field>
        <Field label="fields.ctgReactive">
          <MiniSelect
            defaultValue={
              sr.ctg_reactive === true
                ? 'reactive'
                : sr.ctg_reactive === false
                  ? 'non-reactive'
                  : ''
            }
            options={[
              { value: 'reactive', label: 'options.reactive' },
              { value: 'non-reactive', label: 'options.nonReactive' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                ctg_reactive: e.target.value === 'reactive',
              })
            }
          />
        </Field>
        <Field label="fields.bppInterpretation">
          <MiniSelect
            defaultValue={(sr.bpp_interpretation as string) || ''}
            options={[
              { value: 'NORMAL', label: 'options.normal88' },
              { value: 'EQUIVOCAL', label: 'options.equivocal68' },
              { value: 'ABNORMAL', label: 'options.abnormal48' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                bpp_interpretation: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function PresentationDelivery() {
  const { t } = useTranslation('Report');
  const { fetalAssessment, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;

  return (
    <>
      <SectionHeading>sections.presentationDelivery</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field
          label="fields.fetalPresentation"
          explainFindingKey="presentation"
        >
          <MiniSelect
            defaultValue={fetalAssessment?.presentation || ''}
            options={OPTIONS.fetalPresentation}
            onChange={(e) =>
              onUpdateFindings?.({
                presentation: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.facing" explainFindingKey="presentation">
          <MiniSelect
            defaultValue={fetalAssessment?.facing || ''}
            options={OPTIONS.facing}
            onChange={(e) =>
              onUpdateFindings?.({
                facing: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.occiputPosition">
          <MiniSelect
            defaultValue={(sr.occiput_position as string) || ''}
            options={[
              { value: 'LOA', label: 'options.loaOptimal' },
              { value: 'ROA', label: 'options.roa' },
              { value: 'OP', label: 'options.opDystocia' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ occiput_position: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.engagement">
          <MiniSelect
            defaultValue={
              sr.engagement_fifths != null ? sr.engagement_fifths + '/5' : ''
            }
            options={['5/5', '4/5', '3/5', '2/5', '1/5', '0/5'].map((v) => ({
              value: v,
              label: v,
            }))}
            onChange={(e) =>
              onUpdateFindings?.({
                engagement_fifths: e.target.value
                  ? parseInt(e.target.value)
                  : null,
              })
            }
          />
        </Field>
        <Field label="fields.nuchalCord">
          <MiniSelect
            defaultValue={(sr.nuchal_cord as string) || ''}
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'single', label: 'options.singleWrap' },
              { value: 'double', label: 'options.doubleWrap' },
              { value: 'knot', label: 'options.suspectedTrueKnot' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                nuchal_cord: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.cordPresentation">
          <MiniSelect
            defaultValue={
              sr.cord_presentation === true
                ? 'present'
                : sr.cord_presentation === false
                  ? 'absent'
                  : ''
            }
            options={[
              { value: 'absent', label: 'options.absentNormal' },
              { value: 'present', label: 'options.presentEmergency' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                cord_presentation: e.target.value === 'present',
              })
            }
          />
        </Field>
        <Field label="fields.breechType">
          <MiniSelect
            defaultValue={(sr.breech_type as string) || ''}
            options={[
              { value: 'FRANK', label: 'options.frank' },
              { value: 'COMPLETE', label: 'options.complete' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ breech_type: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.deliveryMode">
          <MiniSelect
            defaultValue={(sr.delivery_mode as string) || ''}
            options={[
              { value: 'VAGINAL', label: 'options.vaginal' },
              { value: 'PLANNED_CAESAREAN', label: 'options.plannedCaesarean' },
              { value: 'INSTRUMENTAL', label: 'options.instrumental' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ delivery_mode: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.plannedDeliveryGa">
          <NumberInput
            placeholder="days"
            step={1}
            defaultValue={
              sr.planned_delivery_ga_days != null
                ? Number(sr.planned_delivery_ga_days)
                : undefined
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                planned_delivery_ga_days: e.target.value
                  ? parseInt(e.target.value)
                  : null,
              })
            }
          />
        </Field>
        <Field label="fields.antenatalSteroids">
          <MiniSelect
            defaultValue={
              sr.antenatal_steroids === true
                ? 'yes'
                : sr.antenatal_steroids === false
                  ? 'no'
                  : ''
            }
            options={OPTIONS.yesNo}
            onChange={(e) =>
              onUpdateFindings?.({
                antenatal_steroids: e.target.value === 'yes',
              })
            }
          />
        </Field>
        <Field label="fields.mgso4">
          <MiniSelect
            defaultValue={
              sr.magnesium_sulfate === true
                ? 'yes'
                : sr.magnesium_sulfate === false
                  ? 'no'
                  : ''
            }
            options={OPTIONS.yesNo}
            onChange={(e) =>
              onUpdateFindings?.({
                magnesium_sulfate: e.target.value === 'yes',
              })
            }
          />
        </Field>
        <Field label="fields.levelOfCare">
          <MiniSelect
            defaultValue={(sr.level_of_care as string) || ''}
            options={[
              { value: 'LEVEL_I', label: 'options.levelI' },
              { value: 'LEVEL_II', label: 'options.levelII' },
              { value: 'LEVEL_III', label: 'options.levelIII' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ level_of_care: e.target.value || null })
            }
          />
        </Field>
      </div>
      <div className="pt-1">
        <Field label="fields.deliveryNotes">
          <textarea
            defaultValue={(sr.delivery_notes as string) || ''}
            placeholder={t('ui.deliveryNotesPlaceholder')}
            rows={2}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 dark:border-gray-600 bg-slate-50 dark:bg-gray-800 text-slate-700 dark:text-gray-200 placeholder-slate-400 dark:placeholder-gray-500 outline-none focus:ring-1 focus:ring-blue-400 resize-none"
            onBlur={(e) =>
              onUpdateFindings?.({
                delivery_notes: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function BiometryGrowth() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;
  const refChart = m.reference_chart != null ? String(m.reference_chart) : '';

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <SectionHeading>sections.biometrySerialGrowth</SectionHeading>
        <MiniSelect
          defaultValue={refChart || 'intergrowth-21st'}
          options={OPTIONS.referenceChart}
          className="w-44 shrink-0"
          onChange={(e) =>
            onUpdateFindings?.({ reference_chart: e.target.value || null })
          }
        />
      </div>
      <BiometryList fields={T3_BIOMETRY} />
      <div className="grid grid-cols-4 gap-1 pt-1">
        <Field label="fields.efwG">
          <NumberInput
            placeholder="g"
            step={1}
            defaultValue={numVal(m, 'efw_g')}
            onBlur={blurHandler('efw_g', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.efwPercentileShort">
          <NumberInput
            placeholder="%"
            step={1}
            defaultValue={numVal(m, 'efw_percentile')}
            onBlur={blurHandler('efw_percentile', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.acVelocity">
          <NumberInput
            placeholder="cm/2wk"
            step={0.1}
            defaultValue={numVal(m, 'ac_velocity_cm_2wks')}
            onBlur={blurHandler('ac_velocity_cm_2wks', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.efwVelocity">
          <NumberInput
            placeholder="g/wk"
            step={5}
            defaultValue={numVal(m, 'efw_velocity_g_week')}
            onBlur={blurHandler('efw_velocity_g_week', onUpdateFindings)}
          />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.ctr" explainMeasKey="MEAS__CTR">
          <NumberInput
            placeholder="~0.45"
            step={0.01}
            defaultValue={numVal(m, 'ctr')}
            onBlur={blurHandler('ctr', onUpdateFindings)}
          />
        </Field>
        <Field
          label="fields.latVentricle"
          explainMeasKey="MEAS__LATERAL_VENTRICLE_WIDTH"
        >
          <NumberInput
            placeholder="≤10"
            defaultValue={numVal(m, 'lateral_ventricle_cm')}
            onBlur={blurHandler('lateral_ventricle_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.corticalGyration">
          <MiniSelect
            defaultValue={(sr.cortical_gyration as string) || ''}
            options={[
              { value: 'normal', label: 'options.normalForGa' },
              { value: 'abnormal', label: 'options.abnormal' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                cortical_gyration: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function DopplerSections() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.umbilicalArteryDoppler</SectionHeading>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.pi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'ua_pi')}
            onBlur={blurHandler('ua_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.percentile">
          <NumberInput
            step={1}
            defaultValue={numVal(m, 'ua_pi_percentile')}
            onBlur={blurHandler('ua_pi_percentile', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.diastolicFlow">
          <MiniSelect
            defaultValue={(sr.ua_diastolic_flow as string) || ''}
            options={OPTIONS.diastolicFlow}
            onChange={(e) =>
              onUpdateFindings?.({ ua_diastolic_flow: e.target.value || null })
            }
          />
        </Field>
      </div>

      <SectionHeading>sections.mcaDopplerCpr</SectionHeading>
      <div className="grid grid-cols-4 gap-1">
        <Field label="fields.mcaPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'mca_pi')}
            onBlur={blurHandler('mca_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.vmaxMom">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'mca_vmax_mom')}
            onBlur={blurHandler('mca_vmax_mom', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.cprShort">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'cpr')}
            onBlur={blurHandler('cpr', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.brainSparing">
          <MiniSelect
            defaultValue={(sr.brain_sparing as string) || ''}
            options={OPTIONS.presentAbsent}
            onChange={(e) =>
              onUpdateFindings?.({ brain_sparing: e.target.value || null })
            }
          />
        </Field>
      </div>

      <SectionHeading>sections.ductusVenosusTruffle</SectionHeading>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.dvPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'dv_pi')}
            onBlur={blurHandler('dv_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.aWave">
          <MiniSelect
            defaultValue={(sr.dv_a_wave as string) || ''}
            options={OPTIONS.aWave}
            onChange={(e) =>
              onUpdateFindings?.({ dv_a_wave: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.uvPulsations">
          <MiniSelect
            defaultValue={
              sr.uv_pulsations === true
                ? 'present'
                : sr.uv_pulsations === false
                  ? 'absent'
                  : ''
            }
            options={[
              { value: 'absent', label: 'options.absentNormal' },
              { value: 'present', label: 'options.presentEmergency' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                uv_pulsations: e.target.value === 'present',
              })
            }
          />
        </Field>
      </div>

      <SectionHeading>sections.uterineArteryPe</SectionHeading>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.rightPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'uta_right_pi')}
            onBlur={blurHandler('uta_right_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.leftPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'uta_left_pi')}
            onBlur={blurHandler('uta_left_pi', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.meanPi">
          <NumberInput
            step={0.01}
            defaultValue={numVal(m, 'uta_mean_pi')}
            onBlur={blurHandler('uta_mean_pi', onUpdateFindings)}
          />
        </Field>
      </div>
    </>
  );
}

function TargetedReview() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.targetedReview</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field label="fields.renalPelvis">
          <NumberInput
            placeholder="<1.0"
            defaultValue={numVal(m, 'renal_pelvis_ap_cm')}
            onBlur={blurHandler('renal_pelvis_ap_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.status">
          <MiniSelect
            defaultValue={(sr.renal_pelvis_status as string) || ''}
            options={[
              { value: 'normal', label: 'options.normal' },
              { value: 'dilated', label: 'options.dilated' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                renal_pelvis_status: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.ventricles">
          <NumberInput
            placeholder="≤1.0"
            defaultValue={numVal(m, 'lateral_ventricle_cm')}
            onBlur={blurHandler('lateral_ventricle_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.trend">
          <MiniSelect
            defaultValue={(sr.ventricle_trend as string) || ''}
            options={[
              { value: 'na', label: 'options.na' },
              { value: 'stable', label: 'options.stable' },
              { value: 'progressive', label: 'options.progressive' },
              { value: 'resolved', label: 'options.resolved' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ ventricle_trend: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.bowelDiam">
          <NumberInput
            placeholder="<1.8"
            step={0.1}
            defaultValue={
              sr.bowel_diameter_cm != null
                ? Number(sr.bowel_diameter_cm)
                : undefined
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                bowel_diameter_cm: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
              })
            }
          />
        </Field>
        <Field label="fields.bowelStatus">
          <MiniSelect
            defaultValue={(sr.bowel_status as string) || ''}
            options={[
              { value: 'normal', label: 'options.normal' },
              { value: 'dilated', label: 'options.dilated18mm' },
              { value: 'echogenic', label: 'options.echogenic' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ bowel_status: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.heartFollowUp">
          <MiniSelect
            defaultValue={(sr.heart_followup as string) || ''}
            options={[
              { value: 'stable', label: 'options.stable' },
              { value: 'effusion', label: 'options.pericardialEffusion' },
              { value: 'arrhythmia', label: 'options.arrhythmia' },
              { value: 'improved', label: 'options.improved' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ heart_followup: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.lungMaturity">
          <MiniSelect
            defaultValue={(sr.lung_maturity as string) || ''}
            options={[
              { value: 'not-assessed', label: 'options.notAssessed' },
              { value: 'mature', label: 'options.matureLiverEcho' },
              { value: 'immature', label: 'options.immature' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ lung_maturity: e.target.value || null })
            }
          />
        </Field>
      </div>
    </>
  );
}

function CervixSection() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const m = measurementValues;

  return (
    <>
      <SectionHeading>sections.cervix</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field label="fields.lengthTvs" explainMeasKey="MEAS__CERVICAL_LENGTH">
          <NumberInput
            placeholder="≥2.5"
            step={0.1}
            defaultValue={numVal(m, 'cervical_length_cm')}
            onBlur={blurHandler('cervical_length_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.funneling">
          <MiniSelect
            defaultValue={
              sr.funneling === true
                ? 'present'
                : sr.funneling === false
                  ? 'absent'
                  : ''
            }
            options={OPTIONS.funneling}
            onChange={(e) =>
              onUpdateFindings?.({ funneling: e.target.value === 'present' })
            }
          />
        </Field>
      </div>
    </>
  );
}

function PlacentaFluid() {
  const {
    fetalAssessment,
    studyReportFields,
    measurementValues,
    onUpdateFindings,
  } = useReportData();
  const sr = studyReportFields;
  const m = measurementValues;
  const mvpCm = numVal(m, 'mvp_cm');
  const calculatedAmnioticFluid = amnioticFluidFromSdpCm(mvpCm);
  const amnioticFluidValue =
    calculatedAmnioticFluid || (sr.fluid_volume as string) || '';

  useEffect(() => {
    if (
      !calculatedAmnioticFluid ||
      calculatedAmnioticFluid === sr.fluid_volume
    ) {
      return;
    }

    void onUpdateFindings?.({ fluid_volume: calculatedAmnioticFluid });
  }, [calculatedAmnioticFluid, onUpdateFindings, sr.fluid_volume]);

  const handleMvpBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value ? parseFloat(e.target.value) : null;
    onUpdateFindings?.({
      mvp_cm: value,
      fluid_volume: amnioticFluidFromSdpCm(value) || null,
    });
  };

  return (
    <>
      <SectionHeading>sections.placentaAmniotic</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field
          label="fields.finalLocation"
          explainFindingKey="placenta_localization"
        >
          <MiniSelect
            defaultValue={fetalAssessment.placentaLocalization || ''}
            options={OPTIONS.placentalLocation}
            onChange={(e) =>
              onUpdateFindings?.({
                placenta_localization: e.target.value || null,
              })
            }
          />
        </Field>
        <Field
          label="fields.placentaCervixClass"
          explainFindingKey="placenta_distance_to_cervix"
        >
          <MiniSelect
            defaultValue={fetalAssessment.placentaCervixClass || ''}
            options={OPTIONS.placentaCervixClass}
            onChange={(e) =>
              onUpdateFindings?.({
                placenta_distance_to_cervix_class: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.grannumGrade">
          <MiniSelect
            defaultValue={
              sr.grannum_grade != null ? String(sr.grannum_grade) : ''
            }
            options={[
              { value: '0', label: '0' },
              { value: '1', label: '1' },
              { value: '2', label: '2' },
              { value: '3', label: 'options.grannum3Term' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                grannum_grade: e.target.value ? parseInt(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="fields.thickness" explainMeasKey="MEAS__PLACENTAL_THICKNESS">
          <NumberInput
            placeholder="<4.0"
            step={0.1}
            defaultValue={numVal(m, 'placental_thickness_cm')}
            onBlur={blurHandler('placental_thickness_cm', onUpdateFindings)}
          />
        </Field>
        <Field label="fields.vasaPraevia">
          <MiniSelect
            defaultValue={
              sr.vasa_praevia === true
                ? 'confirmed'
                : sr.vasa_praevia === false
                  ? 'absent'
                  : ''
            }
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'confirmed', label: 'options.confirmedHospitalise' },
              { value: 'suspected', label: 'options.suspected' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                vasa_praevia: e.target.value !== 'absent',
              })
            }
          />
        </Field>
        <Field label="fields.accretaSpectrum">
          <MiniSelect
            defaultValue={(sr.accreta_spectrum as string) || ''}
            options={[
              { value: 'normal', label: 'options.normal' },
              { value: 'suspicious', label: 'options.suspicious' },
              { value: 'confirmed', label: 'options.confirmed' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                accreta_spectrum: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.cord">
          <MiniSelect
            defaultValue={(sr.cord_vessels as string) || ''}
            options={OPTIONS.cordVessels}
            onChange={(e) =>
              onUpdateFindings?.({ cord_vessels: e.target.value || null })
            }
          />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-1">
        <Field label="fields.amnioticFluid">
          <MiniSelect
            value={amnioticFluidValue}
            options={OPTIONS.amnioticFluid}
            disabled
          />
        </Field>
        <Field label="fields.mvp" explainMeasKey="MEAS__SDP">
          <NumberInput
            placeholder="2–8"
            defaultValue={mvpCm}
            onBlur={handleMvpBlur}
          />
        </Field>
        <Field label="fields.afi">
          <NumberInput
            placeholder="5–25"
            step={0.5}
            defaultValue={numVal(m, 'afi_cm')}
            onBlur={blurHandler('afi_cm', onUpdateFindings)}
          />
        </Field>
      </div>
    </>
  );
}

export function T3Form() {
  const { reportDetailMode } = useReportData();
  const isFullReport = reportDetailMode === 'full';

  if (!isFullReport) {
    return (
      <TwoColumnLayout
        left={
          <>
            <VitalityBPP />
            <BiometryGrowth />
          </>
        }
        right={
          <>
            <PresentationDelivery />
            <PlacentaFluid />
            <ScreeningSection />
          </>
        }
      />
    );
  }

  return (
    <TwoColumnLayout
      left={
        <>
          <VitalityBPP />
          <BiometryGrowth />
          <DopplerSections />
          <TargetedReview />
        </>
      }
      right={
        <>
          <PresentationDelivery />
          <FetalAnatomySection />
          <PlacentaFluid />
          <CervixSection />
          <ScreeningSection />
        </>
      }
    />
  );
}
